// src/pages/msp-inventory/hooks/useSortAndPaginate.js
import { useState, useMemo, useEffect, useRef } from "react";

const PAGE_SIZE = 50;

// Client-side sort + pagination shared by every report table. Returns the
// FULL sorted array (for feeding into image backfill, which must always walk
// top-to-bottom of the WHOLE table, not just the visible page) plus the
// current page's slice and pagination controls.
export const useSortAndPaginate = (items, initialSort = { key: null, dir: "desc" }, storageKey = null) => {
    const [sortConfig, setSortConfig] = useState(() => {
        if (!storageKey) return initialSort;
        try {
            const raw = localStorage.getItem(`${storageKey}:sort`);
            return raw ? JSON.parse(raw) : initialSort;
        } catch { return initialSort; }
    });
    const [page, setPage] = useState(() => {
        if (!storageKey) return 1;
        try {
            const raw = localStorage.getItem(`${storageKey}:page`);
            return raw ? JSON.parse(raw) : 1;
        } catch { return 1; }
    });

    useEffect(() => {
        if (!storageKey) return;
        try { localStorage.setItem(`${storageKey}:sort`, JSON.stringify(sortConfig)); } catch { /* ignore */ }
    }, [storageKey, sortConfig]);
    useEffect(() => {
        if (!storageKey) return;
        try { localStorage.setItem(`${storageKey}:page`, JSON.stringify(page)); } catch { /* ignore */ }
    }, [storageKey, page]);

    const sorted = useMemo(() => {
        if (!items) return [];
        if (!sortConfig.key) return items;
        const arr = [...items];

        // A value "looks numeric" if it's already a number, or a non-empty
        // string that parses cleanly to one (e.g. "7", "30.5") — this is
        // what was missing before: numeric fields returned as STRINGS (a
        // real risk from JSON/Postgres serialization) were being compared
        // lexicographically ("7" > "30" as characters), which is exactly
        // why a small number like 7 could outrank 30 or 1000 when sorting
        // "highest first."
        const looksNumeric = (v) =>
            v !== null && v !== undefined && v !== "" &&
            !Array.isArray(v) && !Number.isNaN(Number(v));

        arr.sort((a, b) => {
            let av = a[sortConfig.key];
            let bv = b[sortConfig.key];

            const aNull = av === null || av === undefined;
            const bNull = bv === null || bv === undefined;
            if (aNull && bNull) return 0;
            if (aNull) return 1;  // nulls always sort last, either direction
            if (bNull) return -1;

            if (looksNumeric(av) && looksNumeric(bv)) {
                av = Number(av);
                bv = Number(bv);
            } else if (typeof av === "string") {
                av = av.toLowerCase();
                bv = typeof bv === "string" ? bv.toLowerCase() : bv;
            }

            if (av < bv) return sortConfig.dir === "asc" ? -1 : 1;
            if (av > bv) return sortConfig.dir === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [items, sortConfig]);

    // Sorting reorders everything, so jump back to page 1 — staying on
    // page 4 of a brand-new order would show a confusing, near-random slice.
    // Skipped on the very first mount when we just restored BOTH sort and
    // page from storage together — otherwise restoring page 3 would
    // immediately get reset to page 1 by this same effect.
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortConfig.key, sortConfig.dir]);
    // If the underlying dataset shrinks below the current page, snap back
    // instead of rendering an empty page.
    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
        if (page > totalPages) setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorted.length]);

    const requestSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key !== key) return { key, dir: "desc" };
            if (prev.dir === "desc") return { key, dir: "asc" };
            return { key: null, dir: "desc" };
        });
    };

    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize: PAGE_SIZE };
};